/**
 * Freeze Account Modal Component
 * Matches the Claude artifact HTML structure
 */

import React, { useState } from 'react';
import './FreezeAccountModal.css';

interface FreezeAccountModalProps {
  userId: string;
  userName: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string, notes: string) => Promise<void>;
}

const FREEZE_REASONS = [
  'AML Investigation',
  'Fraud Suspected',
  'Network Cluster Risk',
  'KYC Failure',
  'Chargeback',
  'Self-Exclusion Violation',
  'Underage Suspicion',
  'Other',
];

const FreezeAccountModal: React.FC<FreezeAccountModalProps> = ({
  userId,
  userName,
  isOpen,
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [notifyEmail, setNotifyEmail] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason) return;

    setLoading(true);
    try {
      await onConfirm(reason, notes);
      // Reset form
      setReason('');
      setNotes('');
      setNotifyEmail(false);
      onClose();
    } catch (error) {
      console.error('Failed to freeze account:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <span className="modal-title">❄️ Freeze Account</span>
          <button className="detail-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body">
          <div className="alert warn" style={{ marginBottom: '18px' }}>
            <div className="alert-icon">⚠️</div>
            <div>
              <div className="alert-title">Freeze {userName}</div>
              <div className="alert-text">User will be unable to log in, deposit, withdraw, or place bets.</div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Freeze Reason *</label>
              <select
                className="form-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
              >
                <option value="">Select reason...</option>
                {FREEZE_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Internal Notes (Audit Log)</label>
              <textarea
                className="form-input form-textarea"
                placeholder="Detailed reason for freezing..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
              />
            </div>

            <div className="form-check">
              <input
                type="checkbox"
                checked={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.checked)}
              />
              <span>Notify user via email</span>
            </div>

            <div className="modal-foot">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="btn btn-danger" disabled={loading || !reason}>
                {loading ? 'Freezing...' : '❄️ Freeze Account'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FreezeAccountModal;

